import React, { useEffect, useState } from 'react';
import { User } from 'types';
import ListHeader from '../holders_list/components/ListHeader';
import EligibilityPopUp from '../holders_list/components/EligibilityComponents/PopUp';
import { createStyles, makeStyles } from '@mui/styles';
import { openPopUp, closePopUp } from 'redux/store/slices/eligibilitySlice';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useAdminMemberSearch } from './hooks/useAdminMemberSearch';
import Control from './components/Controls/Control';
import SearchLoading from './components/Search Results/SearchLoading';
import SearchEmpty from './components/Search Results/SearchEmpty';
import SearchResults from './components/Search Results/SearchResults';
import AdminNavSelector from './components/AdminNavSelector';
import MemberGroupList from './components/Groups/MemberGroupList';
import MyMemberList from './components/MyMemberList';
import useDeploymentMemberGroup from './hooks/useDeploymentMemberGroup';
import useOrganizationMemberGroup from './hooks/useOrganizationMemberGroup';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh',
            overflow: 'auto',
            width: '100%',
            gap: '32px',
        },
        placeholderContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            flex: 1,
            borderRadius: '12px'
        },
        deploymentView: {
            backgroundColor: '#E3F2FD', // Light blue
            color: '#1976D2'
        },
        organizationView: {
            backgroundColor: '#FFEBEE', // Light red
            color: '#D32F2F'
        },
        placeholderText: {
            fontSize: '24px',
            fontWeight: 600,
            textAlign: 'center'
        }
    })
);

type AdminHomepageProps = {
    user: User;
    layoutTitles: { title: string; subtitle: string };
    setLayoutTitles: (title: string, subtitle: string) => void;
    resetCatchUpItemStatus: () => void;
    resetHomelessInfo: () => void;
    resetMessageInfo: () => void;
    resetMemberReferrals: () => void;
    resetTouchpointAnswers: () => void;
    resetUnenrollmentState: () => void;
}

const AdminHomepage: React.FC<AdminHomepageProps> = ({
    user,
    layoutTitles,
    setLayoutTitles,
    resetCatchUpItemStatus,
    resetHomelessInfo,
    resetMessageInfo,
    resetMemberReferrals,
    resetTouchpointAnswers,
    resetUnenrollmentState
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { query, setQuery, results, isLoading } = useAdminMemberSearch();
    const [activeView, setActiveView] = useState('Members by Deployments');
    const deploymentHook = useDeploymentMemberGroup();
    const organizationHook = useOrganizationMemberGroup();

    const isEligibilityPopUpOpen = useSelector((state: any) => state.eligibility.isPopUpOpen);
    const isSearching = query.trim().length > 0;

    const navItems = ['Members by Deployments', 'Members by Organizations', 'My Members'];
    
    useEffect(() => {
        if (layoutTitles.subtitle !== 'Admin Member List') {
            setLayoutTitles('', 'Admin Member List');
        }

        resetCatchUpItemStatus();
        resetHomelessInfo();
        resetMessageInfo();
        resetMemberReferrals();
        resetTouchpointAnswers();
        resetUnenrollmentState();
    }, [
        layoutTitles.subtitle,
        setLayoutTitles,
        resetCatchUpItemStatus,
        resetHomelessInfo,
        resetMessageInfo,
        resetMemberReferrals,
        resetTouchpointAnswers,
        resetUnenrollmentState
    ]);

    const renderListView = () => {
        const isDeploymentView = activeView === 'Members by Deployments';
        const isMyMemberView = activeView === 'My Members';
        
        if (isMyMemberView) {
            return <MyMemberList />;
        }
        
        if (isDeploymentView) {
            return <MemberGroupList type="deployment" hook={deploymentHook} />;
        }
        
        return <MemberGroupList type="organization" hook={organizationHook} />;
    };

    return (
        <div className={classes.root}>
            <Helmet title="Admin Member List" />
            <ListHeader 
                setEligibilityPopUp={() => dispatch(openPopUp())}
                shouldShowSignupButton={true}
                shouldDisableSignupButton={false}
            />
            <Control 
                query={query}
                setQuery={setQuery}
            />
            {isSearching ? (
                isLoading ? (
                    <SearchLoading />
                ) : results.length > 0 ? (
                    <SearchResults members={results} />
                ) : (
                    <SearchEmpty />
                )
            ) : (
                <>
                    <AdminNavSelector
                        active={activeView}
                        setActive={setActiveView}
                        navItems={navItems}
                    />
                    {renderListView()}
                </>
            )}
            {isEligibilityPopUpOpen && (
                <EligibilityPopUp
                    handleCloseClick={() => dispatch(closePopUp())}
                    user={user}
                />
            )}
        </div>
    );
};

export default AdminHomepage; 